import { Fragment, memo, useMemo, useRef } from "react";
import {
  Brush,
  CartesianGrid,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { OnBrushUpdate } from "recharts/types/context/brushUpdateContext";
import type { FeatureData, TICData } from "@/types/spectrum";
import { getFeatureColor } from "@/utils/featureMapping";
import { groupFeaturesByObservation } from "@/utils/observationGrouping";
import { panelCls } from "@/utils/panelCls";

interface TICPlotProps {
  ticData: TICData[];
  features: FeatureData[];
  selectedFeature?: string;
  onTICZoom: OnBrushUpdate;
  onFeatureSelect: (featureId: string) => void;
  onChartClick?: (rt: number) => void;
}

// TIC Plot 헤더. 시료 정보는 상단 SampleInfoCollapsedHeader(분석 실제 시료)에
// 표시되므로 차트 헤더는 차트 정체성만 담는다 (데이터 출처 selector 제거 후).
const TICPlotHeader = memo(() => {
  return (
    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center flex-wrap">
      <span className="w-2 h-2 bg-purple-500 rounded-full mr-2"></span>
      <span>TIC Plot</span>
      <span className="ml-2 text-sm font-normal text-gray-500">
        (총이온크로마토그램)
      </span>
    </h3>
  );
});
TICPlotHeader.displayName = "TICPlotHeader";

const TICPlot = ({
  ticData,
  features,
  selectedFeature,
  onTICZoom,
  onFeatureSelect,
  onChartClick,
}: TICPlotProps) => {
  // X축 ticks 설정: 0부터 최댓값 ceil까지 정수 1단위
  const maxRT = Math.max(...ticData.map((t) => t.rt));
  const maxTick = Math.ceil(maxRT);

  // Sprint 22 #9: wrapper div onClick + 픽셀 → RT 변환
  // T1: 차트 선의 단위는 후보가 아니라 관찰(스캔)
  const observations = useMemo(
    () => groupFeaturesByObservation(features),
    [features],
  );
  const wrapperRef = useRef<HTMLDivElement>(null);
  const mouseDownXRef = useRef<number | null>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    mouseDownXRef.current = e.clientX;
  };

  const handleWrapperClick = (e: React.MouseEvent) => {
    // (1) Brush displacement guard — 5px 이상 drag면 skip
    if (
      mouseDownXRef.current !== null &&
      Math.abs(e.clientX - mouseDownXRef.current) >= 5
    ) {
      return;
    }

    // (2) ReferenceLine 클릭은 기존 onFeatureSelect로 처리 (wrapper skip)
    if ((e.target as HTMLElement).closest(".recharts-reference-line")) {
      return;
    }

    // (3) 픽셀 → RT 변환
    const grid = wrapperRef.current?.querySelector(".recharts-cartesian-grid");
    if (!grid) return;
    const rect = grid.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    if (relX < 0 || relX > rect.width) return;
    if (ticData.length === 0) return;
    const ratio = relX / rect.width;
    const minRT = ticData[0].rt;
    const clickedRT = minRT + ratio * (maxRT - minRT);

    onChartClick?.(clickedRT);
  };

  return (
    <div
      className={panelCls("standard", "justify-between flex flex-col h-full")}
    >
      <TICPlotHeader />
      {/* biome-ignore lint/a11y/useSemanticElements: ResponsiveContainer 내부 SVG 컨테이너는 <button> 중첩 불가 — div+role="button"으로 a11y 지원 */}
      <div
        ref={wrapperRef}
        role="button"
        tabIndex={0}
        aria-label="TIC Plot 클릭 — 가장 가까운 관찰(스캔) 선택"
        className="relative cursor-crosshair outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/40 focus-visible:ring-offset-2 focus-visible:rounded-sm"
        onMouseDown={handleMouseDown}
        onClick={handleWrapperClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            handleWrapperClick(e as unknown as React.MouseEvent);
          }
        }}
      >
        <ResponsiveContainer
          debounce={0}
          width="100%"
          height={300}
          className="pb-10"
        >
          <LineChart data={ticData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis
              dataKey="rt"
              type="number"
              domain={["dataMin", "dataMax"]}
              tickCount={maxTick + 1}
              label={{
                value: "RT (min)",
                position: "bottom",
                style: { textAnchor: "middle", fill: "#64748b" },
              }}
              tick={{ fontSize: 12, fill: "#64748b" }}
            />
            <YAxis
              label={{
                value: "Intensity",
                angle: -90,
                position: "insideLeft",
                style: { textAnchor: "middle", fill: "#64748b" },
              }}
              tick={{ fontSize: 12, fill: "#64748b" }}
            />
            <Tooltip
              formatter={(value) => [value, "Intensity"]}
              labelFormatter={(label) => `RT: ${label} min`}
              contentStyle={{
                backgroundColor: "white",
                border: "1px solid #e2e8f0",
                borderRadius: "8px",
              }}
            />
            <Line
              isAnimationActive={false}
              type="monotone"
              dataKey="intensity"
              stroke="#8b5cf6"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6, fill: "#ef4444" }}
            />
            {/* 관찰(스캔) 위치에 수직선 표시 - 클릭 가능.
                T1: 같은 스캔 후보 N개는 선 하나 — 대표(1순위) 후보의 색을 쓰고,
                그 관찰의 어느 후보가 선택돼 있어도 강조한다. 클릭은 선택된 후보가
                이 관찰에 있으면 그 후보를(토글), 없으면 대표 후보를 고른다. */}
            {/* Sprint 22 #9: invisible padding ReferenceLine (strokeWidth=20) hit area 확대 */}
            {observations.map((observation) => {
              const representative = observation.features[0];
              const selectedHere = observation.features.find(
                (f) => f.feature_id === selectedFeature,
              );
              const target = selectedHere ?? representative;
              const isActive = selectedHere != null;
              return (
                <Fragment key={observation.id}>
                  {/* Invisible hit area — strokeWidth=20으로 클릭 영역 확대 */}
                  <ReferenceLine
                    x={observation.rt}
                    stroke="transparent"
                    strokeWidth={20}
                    onClick={() => onFeatureSelect(target.feature_id)}
                    style={{ cursor: "pointer" }}
                  />
                  {/* Visible observation line */}
                  <ReferenceLine
                    x={observation.rt}
                    stroke={getFeatureColor(representative.feature_id)}
                    strokeDasharray={isActive ? "0" : "3 3"}
                    strokeWidth={isActive ? 3 : 2}
                    onClick={() => onFeatureSelect(target.feature_id)}
                    cursor="pointer"
                  />
                </Fragment>
              );
            })}
            {/* 줌 컨트롤 */}
            <Brush
              dataKey="rt"
              height={30}
              stroke="#8b5cf6"
              onChange={onTICZoom}
              startIndex={0}
              endIndex={ticData.length - 1}
              travellerWidth={10}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default memo(TICPlot);
